import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import RouterHistory from '@reverbdotcom/commons/src/cms/lib/router_history';
import Jumplinks, { IJumplink } from '@reverbdotcom/commons/src/components/jumplinks';
import { RCImage, RCStarRating, useIntersectionObserver } from '@reverbdotcom/cadence/components';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { Core_Csp_LayoutQueryQuery } from '@reverbdotcom/commons/src/gql/graphql';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import { gql } from '@reverbdotcom/commons/src/gql';

export const JUMPLINK_LISTINGS = 'listings';
export const JUMPLINK_PRODUCT_DETAILS = 'product-details';
export const JUMPLINK_PRICEGUIDE = 'price-guide';
export const JUMPLINK_SIMILAR = 'similar-products';
export const JUMPLINK_REVIEWS = 'reviews-section';
export const JUMPLINK_BLURB = 'blurb';
export const JUMPLINK_GALLERY = 'gallery';
export const JUMPLINK_VIDEO = 'video';
export const JUMPLINK_SPECS = 'specs';
export const JUMPLINK_OVERVIEW = 'overview';

export const JUMPLINK_ORDER = [
  JUMPLINK_LISTINGS,
  JUMPLINK_SIMILAR,
  JUMPLINK_PRODUCT_DETAILS,
  JUMPLINK_GALLERY,
  JUMPLINK_SPECS,
  JUMPLINK_OVERVIEW,
  JUMPLINK_VIDEO,
  JUMPLINK_PRICEGUIDE,
  JUMPLINK_REVIEWS,
  JUMPLINK_BLURB,
];

export interface IJumplinks {
  [JUMPLINK_LISTINGS]?: boolean;
  [JUMPLINK_PRODUCT_DETAILS]?: boolean;
  [JUMPLINK_OVERVIEW]?: boolean;
  [JUMPLINK_SPECS]?: boolean;
  [JUMPLINK_VIDEO]?: boolean;
  [JUMPLINK_GALLERY]?: boolean;
  [JUMPLINK_SIMILAR]?: boolean;
  [JUMPLINK_PRICEGUIDE]?: boolean;
  [JUMPLINK_REVIEWS]?: boolean;
  [JUMPLINK_BLURB]?: boolean;
}

interface IExternalProps {
  csp?: Core_Csp_LayoutQueryQuery['csp'];
  user: Partial<IUser>;
  activeJumplink: string | undefined;
}

export type IJumplinkProps = IExternalProps & IJumplinks;

const SIDEBAR_JUMPLINKS = [
  JUMPLINK_OVERVIEW,
  JUMPLINK_VIDEO,
  JUMPLINK_GALLERY,
  JUMPLINK_SPECS,
];

export function CSPJumplinks({ csp = {}, user, activeJumplink, ...props }: IJumplinkProps) {
  const navObserver = useIntersectionObserver({ threshold: 1 });
  const showStickyNav = navObserver.entry?.intersectionRatio < 1;

  function buildJumplink(id: string): IJumplink {
    if (!props[id]) return null;
    const text = I18n.t(`discovery.comparisonShoppingPages.jumpLinks.${id}`);
    const stats = csp?.productReviewSearch?.stats;

    let content: string | JSX.Element;
    if (id === JUMPLINK_REVIEWS && !!stats) {
      content = (
        <div className="d-flex fx-align-center">
          {text}
          {stats.total > 0 &&
            <div className="ml-2">
              <RCStarRating
                rating={stats.averageRating / 5.0}
                count={stats.total}
              />
            </div>
          }
        </div>
      );
    } else {
      content = text;
    }

    const baseJumplink = {
      id,
      text: content,
      inSidebar: SIDEBAR_JUMPLINKS.indexOf(id) > -1,
      isActive: id === activeJumplink,
    };

    const clickEvent = {
      componentName: 'CSP2Jumplinks',
      eventName: MParticleEventName.ClickedJumplink,
      entityId: id,
      cspId: csp?.id,
      cspSlug: csp?.slug,
      experiments: formatExperimentsForEventAttributes(user, []),
    };

    return {
      ...baseJumplink,
      clickEvent,
    };
  }

  const dropdownJumplinks = () => (
    JUMPLINK_ORDER.filter((name) => props[name]).map((name) => (
      <option key={name} value={name}>
        {I18n.t(`discovery.comparisonShoppingPages.jumpLinks.${name}`)}
      </option>
    ))
  );

  const cspImage = csp?.cspImage?.[0]?.source;

  const DesktopJumplinks = ({ imageSrc }: { imageSrc?: string }) => {
    return <div className="csp2-jumplinks--hide-mobile">
      <Jumplinks
        jumplinks={JUMPLINK_ORDER.map(buildJumplink).filter(Boolean)}
        image={imageSrc}
      />
    </div>;
  };

  const MobileJumplinks = ({ imageSrc }: { imageSrc?: string }) => {
    const dropdownId = `csp2-jumplinks-${imageSrc && '-image'}-dropdown`;

    return <div className="csp2-jumplinks__dropdown">
      {imageSrc && <div className="csp2-jumplinks__dropdown__image">
        <RCImage
          src={cspImage}
          alt={csp.title}
          imageBox="auto"
          overlay="offwhite"
          borderRadius="md"
        />
      </div>}
      <div className="csp2-jumplinks__dropdown__inner">
        <label htmlFor={dropdownId} className="csp2-jumplinks__dropdown__title">
          {I18n.t('discovery.comparisonShoppingPages.jumpTo')}:
        </label>
        <select
          id={dropdownId}
          className={classNames({ 'csp2-jumplinks__dropdown__selected': window.location.hash })}
          value={activeJumplink || 'placeholder'}
          onChange={(e) => {
            const location = {
              hash: '#' + e.target.value,
              pathname: window.location.pathname,
            };

            if (e.target.value.includes(JUMPLINK_PRICEGUIDE)) {
              trackEvent({
                componentName: 'CSP2Jumplinks',
                eventName: MParticleEventName.ClickedJumplink,
                cspId: csp?.id,
                cspSlug: csp?.slug,
                entityId: e.target.value,
                experiments: formatExperimentsForEventAttributes(user, []),
              });
            }

            RouterHistory.push(location);
          }}
        >
          <option disabled value="placeholder">
            {I18n.t('discovery.comparisonShoppingPages.jumpLinks.placeholder')}
          </option>
          {dropdownJumplinks()}
        </select>
      </div>
    </div>;
  };

  return (
    <>
      {/* When this goes off the screen */}
      <div ref={navObserver.setTrackedRef} className="csp2-jumplinks--not-sticky">
        <MobileJumplinks />
        <DesktopJumplinks />
      </div>

      {/* Then this appears */}
      <div className={classNames('csp2-jumplinks--pinned',
        { 'csp2-jumplinks--slidein': showStickyNav },
      )}>
        <MobileJumplinks imageSrc={cspImage} />
        <DesktopJumplinks imageSrc={cspImage} />
      </div>
    </>
  );
}

export const CSPJumplinksFragment = gql(`
  fragment CSPReviewJumplinksData on CSP {
    _id
    productReviewSearch(
      input: {
        limit: 0
      }
    ) {
      stats {
        total
        averageRating
      }
    }
  }
`);

export default CSPJumplinks;
